import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";

const LINKS = [
  { id: "showcase", label: "Showcase" },
  { id: "manifesto", label: "Manifesto" },
  { id: "order", label: "Quick Order" },
  { id: "contact", label: "Contact" },
];

export default function Navbar({ onNavigate }) {
  const [open, setOpen] = useState(false);

  const go = (id) => {
    setOpen(false);
    onNavigate(id);
  };

  return (
    <header
      data-testid="site-navbar"
      className="sticky top-0 z-50 backdrop-blur-md bg-cream-50/85 border-b border-cream-200"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[72px] flex items-center justify-between">
        <button
          data-testid="navbar-brand"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-3 group"
        >
          <img
            src="/assets/azh-logo.webp"
            alt="Az Haniza Enterprise logo"
            className="h-10 w-10 object-cover rounded-full border border-cream-200"
          />
          <span className="font-poppins font-semibold text-base sm:text-lg text-charcoal-900">
            Az Haniza Enterprise
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-8" data-testid="navbar-links">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`navbar-link-${l.id}`}
              onClick={() => go(l.id)}
              className="text-xs uppercase tracking-[0.2em] font-medium text-charcoal-700 hover:text-taupe-600 transition-colors duration-200"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="navbar-order-button"
            onClick={() => go("order")}
            className="group flex items-center gap-2 bg-charcoal-900 text-cream-50 text-xs uppercase tracking-[0.2em] font-medium px-5 py-2.5 rounded-full hover:bg-taupe-700 transition-colors duration-300"
          >
            Place Order
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </nav>

        <button
          data-testid="navbar-mobile-toggle"
          className="md:hidden text-charcoal-900"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="navbar-mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="md:hidden overflow-hidden bg-cream-50/95 backdrop-blur-md border-t border-cream-200"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`navbar-mobile-link-${l.id}`}
                  onClick={() => go(l.id)}
                  className="text-left text-sm uppercase tracking-[0.2em] font-medium text-charcoal-800"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="navbar-mobile-order-button"
                onClick={() => go("order")}
                className="mt-2 bg-charcoal-900 text-cream-50 text-xs uppercase tracking-[0.2em] font-medium px-5 py-3 rounded-full w-fit"
              >
                Place Order
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
